import { metaData, Enums } from '@cornerstonejs/core';
import cornerstoneDICOMImageLoader from '@cornerstonejs/dicom-image-loader';
import { t, setLanguage, getLanguage, SUPPORTED_LANGUAGES } from '@/i18n';

/**
 * Reads a DICOM tag from a wadors metadata JSON.
 * Returns null if missing.
 */
function _getTag(meta, tag, formatter = v => v) {
    const entry = meta?.[tag];
    if (!entry?.Value || entry.Value.length === 0) return null;
    return formatter(entry.Value[0]);
}

function _getTagArray(meta, tag) {
    return meta?.[tag]?.Value ?? null;
}

/**
 * Builds the HTML contents of the metadata popover from the raw DICOM JSON of
 * the first instance of the series.
 */
function _buildMetadataHTML(meta, numSlices, rmvCode) {
    const fmtDate = (v) => (v && v.length >= 8) ? `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}` : v;
    const fmtTime = (v) => (v && v.length >= 6) ? `${v.slice(0, 2)}:${v.slice(2, 4)}:${v.slice(4, 6)}` : v;
    const fmtAge = (v) => {
        // DICOM AS: "045Y" / "036M" / etc.
        if (!v) return null;
        const m = /^(\d+)([YMWD])$/.exec(v);
        if (!m) return v;
        const unit = t(`metadata.age_unit.${m[2]}`);
        return `${parseInt(m[1], 10)} ${unit}`;
    };
    const fmtSpacing2D = (arr) => (arr && arr.length >= 2) ? `${(+arr[0]).toFixed(2)} × ${(+arr[1]).toFixed(2)} mm` : null;
    const fmtPos3D = (arr) => (arr && arr.length >= 3) ? arr.map(v => (+v).toFixed(2)).join(', ') : null;
    const fmtMm = (v) => `${v} mm`;
    const fmtMs = (v) => `${v} ms`;
    const fmtImageType = (arr) => arr ? arr.join(' \\ ') : null;

    // Calculated: total Z coverage in mm
    const spacing = _getTag(meta, '00180088');
    const coverage = (spacing && numSlices) ? `${(numSlices * +spacing).toFixed(1)} mm` : null;

    // Calculated: image size
    const rows = _getTag(meta, '00280010');
    const cols = _getTag(meta, '00280011');
    const imageSize = (rows && cols) ? `${rows} × ${cols} px` : null;

    // Calculated: temporal position summary
    const tposId = _getTag(meta, '00200100');
    const tposTotal = _getTag(meta, '00200105');
    const tposSummary = (tposId && tposTotal)
        ? t('metadata.temporal_position_fmt', { id: tposId, total: tposTotal })
        : (tposTotal ?? tposId);

    // Window center/width can be single values or arrays of presets
    const wc = _getTagArray(meta, '00281050');
    const ww = _getTagArray(meta, '00281051');
    const fmtWlPair = (wcArr, wwArr) => {
        if (!wcArr || !wwArr) return null;
        return wcArr.map((c, i) => `C ${(+c).toFixed(0)} / W ${(+wwArr[i] ?? 0).toFixed(0)}`).join(' · ');
    };

    // [sectionKey, [[fieldKey, value], ...]] — labels resolved via i18n at render time
    const sections = [
        ['patient', [
            ['patient_id', _getTag(meta, '00100020')],
            ['sex', _getTag(meta, '00100040')],
            ['age', _getTag(meta, '00101010', fmtAge)],
            ['position', _getTag(meta, '00185100')],
        ]],
        ['study', [
            ['test_id', rmvCode],
            ['study_description', _getTag(meta, '00081030')],
            ['accession_number', _getTag(meta, '00080050')],
            ['study_date', _getTag(meta, '00080020', fmtDate)],
            ['study_time', _getTag(meta, '00080030', fmtTime)],
            ['modality', _getTag(meta, '00080060')],
            ['body_part', _getTag(meta, '00180015')],
        ]],
        ['equipment', [
            ['manufacturer', _getTag(meta, '00080070')],
            ['model', _getTag(meta, '00081090')],
            ['institution', _getTag(meta, '00080080')],
        ]],
        ['series', [
            ['series_description', _getTag(meta, '0008103E')],
            ['series_number', _getTag(meta, '00200011')],
            ['acquisition_number', _getTag(meta, '00200012')],
            ['protocol_name', _getTag(meta, '00181030')],
            ['image_type', fmtImageType(_getTagArray(meta, '00080008'))],
            ['temporal_position', tposSummary],
            ['slices', numSlices],
        ]],
        ['geometry', [
            ['image_size', imageSize],
            ['pixel_spacing', fmtSpacing2D(_getTagArray(meta, '00280030'))],
            ['slice_thickness', _getTag(meta, '00180050', fmtMm)],
            ['spacing_between_slices', _getTag(meta, '00180088', fmtMm)],
            ['total_coverage', coverage],
            ['first_slice_position', fmtPos3D(_getTagArray(meta, '00200032'))],
            ['frame_of_reference', _getTag(meta, '00200052')],
        ]],
        ['technique_mr', [
            ['magnetic_field', _getTag(meta, '00180087', v => `${v} T`)],
            ['scanning_sequence', _getTag(meta, '00180020')],
            ['repetition_time', _getTag(meta, '00180080', fmtMs)],
            ['echo_time', _getTag(meta, '00180081', fmtMs)],
            ['inversion_time', _getTag(meta, '00180082', fmtMs)],
            ['echo_train_length', _getTag(meta, '00180091')],
            ['flip_angle', _getTag(meta, '00181314', v => `${v}°`)],
        ]],
        ['technique_ct', [
            ['kvp', _getTag(meta, '00180060', v => `${v} kV`)],
            ['tube_current', _getTag(meta, '00181151', v => `${v} mA`)],
            ['exposure_time', _getTag(meta, '00181150', fmtMs)],
            ['convolution_kernel', _getTag(meta, '00181210')],
            ['spiral_pitch', _getTag(meta, '00189311')],
        ]],
        ['visualization', [
            ['window_level', fmtWlPair(wc, ww)],
            ['rescale_slope', _getTag(meta, '00281053')],
            ['rescale_intercept', _getTag(meta, '00281052')],
        ]],
    ];

    let html = '';
    for (const [sectionKey, items] of sections) {
        const visible = items.filter(([, v]) => v != null && v !== '');
        if (visible.length === 0) continue;
        html += `<h4>${t(`metadata.section.${sectionKey}`)}</h4><dl>`;
        for (const [fieldKey, value] of visible) {
            html += `<dt>${t(`metadata.field.${fieldKey}`)}</dt><dd>${value}</dd>`;
        }
        html += '</dl>';
    }
    return html || `<p style="margin:0">${t('metadata.no_data')}</p>`;
}

const LANGUAGE_LABELS = { es: 'ES', gl: 'GL', en: 'EN' };
const LANGUAGE_FULL_NAMES = { es: 'Español', gl: 'Galego', en: 'English' };

/**
 * Wires the language switcher dropdown: populates the options from i18n's
 * SUPPORTED_LANGUAGES, sets the current language as selected, and calls
 * setLanguage() on change (which triggers all language-changed listeners).
 */
export function setupLanguageSwitcher() {
    const select = document.getElementById('language-switcher');
    if (!select) return;

    SUPPORTED_LANGUAGES.forEach(lang => {
        const opt = document.createElement('option');
        opt.value = lang;
        opt.textContent = LANGUAGE_LABELS[lang] ?? lang;
        opt.title = LANGUAGE_FULL_NAMES[lang] ?? lang;  // Hover muestra el nombre completo
        if (lang === getLanguage()) opt.selected = true;
        select.appendChild(opt);
    });

    select.addEventListener('change', (e) => {
        setLanguage(e.target.value);
    });
}

/**
 * Wires the metadata info button + popover. Reads tags from the first imageId
 * via cornerstoneDICOMImageLoader.wadors.metaDataManager, populates the popover
 * once, and toggles its visibility on button click. Clicking outside closes it.
 */
export function setupMetadataPopover(imageIds, rmvCode) {
    const button = document.getElementById('show_metadata');
    const popover = document.getElementById('metadata-popover');
    if (!button || !popover || !imageIds || imageIds.length === 0) {
        return;
    }

    const firstMeta = cornerstoneDICOMImageLoader.wadors.metaDataManager.get(imageIds[0]);
    const rebuild = () => { popover.innerHTML = _buildMetadataHTML(firstMeta, imageIds.length, rmvCode); };
    rebuild();
    window.addEventListener('language-changed', rebuild);

    button.onclick = (e) => {
        e.stopPropagation();
        const isVisible = popover.style.display !== 'none';
        popover.style.display = isVisible ? 'none' : 'block';
    };

    document.addEventListener('click', (e) => {
        if (popover.style.display === 'none') return;
        if (popover.contains(e.target)) return;
        if (button.contains(e.target)) return;
        popover.style.display = 'none';
    });
}

/**
 * Function that allow to read a especific url parameter
 *
 * @param {*} name name of the parameter that you want to read
 * @returns return the value of the parameter
 */
export function getQueryParam(name) {
    const params = new URLSearchParams(window.location.search);
    return params.has(name) ? decodeURIComponent(params.get(name)) : null;
  }

/**
 * Computes the AXIAL viewport orientation from the DICOM ImageOrientationPatient
 * tag of the first image. Returns the patient-axial enum for axis-aligned
 * acquisitions, and explicit { viewPlaneNormal, viewUp } vectors for oblique
 * acquisitions so the primary viewport aligns with the actual acquired plane.
 */
export function getAxialOrientationFromImage(imageId) {
    const imagePlane = metaData.get('imagePlaneModule', imageId);
    if (!imagePlane || !imagePlane.rowCosines || !imagePlane.columnCosines) {
        console.warn('[orientation] no imagePlaneModule available, falling back to AXIAL enum');
        return Enums.OrientationAxis.AXIAL;
    }

    const r = imagePlane.rowCosines;
    const c = imagePlane.columnCosines;

    // Slice normal = col × row. Cornerstone's viewPlaneNormal points OUT of the
    // screen toward the observer; the radiological convention places the observer
    // "at the feet" looking toward the head, which corresponds to col × row (i.e.
    // the negative of row × col).
    const n = [
        c[1] * r[2] - c[2] * r[1],
        c[2] * r[0] - c[0] * r[2],
        c[0] * r[1] - c[1] * r[0],
    ];

    // Pure-axial acquisition → use the enum (matches Cornerstone's reference behavior)
    if (Math.abs(Math.abs(n[2]) - 1) < 1e-6) {
        return Enums.OrientationAxis.AXIAL;
    }

    // Oblique acquisition → use the acquisition's own orientation.
    // viewUp = inverted column direction so anatomical anterior appears up on screen.
    return {
        viewPlaneNormal: n,
        viewUp: [-c[0], -c[1], -c[2]],
    };
}

/**
 * Funtion that update the annotations that are visible, depends of the class that 
 * the user wants to see (liver, IVC, aorta)
 * 
 * @param {*} all all of the classes or a specific class
 * @param {*} annotations all the annotations in the system
 * @param {*} activeSegmentIndex the especific class that is selected
 */
export function updateActiveAnnotation(all, annotations, activeSegmentIndex){
  const showAllButton = document.getElementById('show_all');

  if (all) {
    // Cambiar imagen a 'showall_icon.png'
    showAllButton.innerHTML = `
      <img src="${require('@/assets/showall_icon.png')}" alt="Show All" style="width: 35px; height: 20px;">
    `;

    console.log("SHOW ALL ANNOTATION CLASSES");
    for (let i=0; i<annotations.length;i++){
      annotations[i].setVisible(true, null);
    }
    
  } else {
    console.log("SHOW ANNOTATION CLASS:", activeSegmentIndex);
    // Cambiar imagen a 'showall_icon.png'
    showAllButton.innerHTML = `
      <img src="${require('@/assets/showone_icon.png')}" alt="Show One" style="width: 35px; height: 20px;">
    `;

    for (let i=0; i<annotations.length;i++){
      annotations[i].setVisible(false, null);
    }
    annotations[activeSegmentIndex-1].setVisible(true, null);
  }
}

export function updateTextBoxView(showTextBox, cornerstoneTools){
  const showAllButton = document.getElementById('show_textbox');
  const style = cornerstoneTools.annotation.config.style.getDefaultToolStyles();

  if (showTextBox) {
    // Cambiar imagen a 'showall_icon.png'
    showAllButton.innerHTML = `
      <img src="${require('@/assets/showtextbox_icon.png')}" alt="Show textbox" style="width: 25px; height: 25px;">
    `;
    
    style.global.textBoxVisibility = showTextBox;
    cornerstoneTools.annotation.config.style.setDefaultToolStyles(style);
    console.log('🔕 TextBoxes ocultos');

  } else {
    // Cambiar imagen a 'showall_icon.png'
    showAllButton.innerHTML = `
      <img src="${require('@/assets/notshowtextbox_icon.png')}" alt="Show One" style="width: 25px; height: 25px;">
    `;

    style.global.textBoxVisibility = showTextBox;
    cornerstoneTools.annotation.config.style.setDefaultToolStyles(style);
    console.log('🔕 TextBoxes ocultos');

  }
}


/**
 * Event listener to cancel the drawing event.
 * 
 * - If the "Escape" key is pressed, active manipulations are canceled.
 * - If the "Backspace" key is pressed, the selected annotation is removed.
 * 
 * @param {Event} evt The event object
 * @param {*} cornerstoneTools The cornerstone tools library
 * @param {*} annotations The list of annotations
 */
export const cancelDrawingEventListener = (evt, cornerstoneTools, annotations) => {
  const { element, key } = evt.detail;
  if (key === 'Escape') {
    if (process.env.VUE_APP_DEBUG === "true") {
        console.log("Escape");
    }
    cornerstoneTools.cancelActiveManipulations(element);
  }
  if (key === 'Backspace') {
    if (process.env.VUE_APP_DEBUG === "true") {
        console.log("backspace");
    }
    var an = cornerstoneTools.annotation.state.getAnnotation(cornerstoneTools.annotation.selection.getAnnotationsSelected()[0]);
    for (let i=0; i<annotations.length;i++){
      annotations[i].remove(an.annotationUID);
    }
    cornerstoneTools.annotation.state.removeAnnotation(cornerstoneTools.annotation.selection.getAnnotationsSelected()[0]);
  }
};

/**
 * Function to cancel the drawing event.
 * 
 * - This function cancels any active manipulations in Cornerstone.
 * - It is triggered when an event occurs.
 * 
 * @param {Event} evt The event object 
 * @param {*} cornerstoneTools cornerstoneTools The cornerstone tools library
 */
export const cancelDrawingEvent = (evt, cornerstoneTools) => {
    if (process.env.VUE_APP_DEBUG === "true") {
        console.log("cancelDrawingEvent");
    }
    const { element} = evt.detail;
    cornerstoneTools.cancelActiveManipulations(element);
  };

export function addDropdownToToolbarAnnotationClasses({
  id,
  options,
  container,
  style,
  labelText,
  labelKey,
  onSelectedValueChange,
}) {
  const { map, defaultValue } = options;

  container = container ?? document.getElementById('toolbox');

  // Wrapper que incluye label, colorBox y select
  const wrapper = document.createElement('div');
  wrapper.style.display = 'flex';
  wrapper.style.alignItems = 'center';
  wrapper.style.gap = '8px';

  if (labelText || labelKey) {
    const label = document.createElement('label');
    label.htmlFor = id;
    label.innerText = labelKey ? t(labelKey) : labelText;
    wrapper.appendChild(label);
    if (labelKey) {
      window.addEventListener('language-changed', () => { label.innerText = t(labelKey); });
    }
  }

  // Cuadrado de color a la izquierda (refleja selección actual)
  const colorBox = document.createElement('div');
  colorBox.style.width = '20px';
  colorBox.style.height = '20px';
  colorBox.style.border = '1px solid #ccc';
  colorBox.style.borderRadius = '4px';
  wrapper.appendChild(colorBox);

  // Dropdown
  const select = document.createElement('select');
  select.id = id;

  if (style) {
    Object.assign(select.style, style);
  }

  // Añadir opciones con cuadrado unicode
  Array.from(map.entries()).forEach(([key, value]) => {
    const optionElement = document.createElement('option');
    optionElement.value = value.id;
    
    // Mostrar cuadrado de color y texto
    optionElement.textContent = `■ ${key}`;
    optionElement.style.color = value.color; // Color solo al carácter "■"

    // Guardar color original como dataset (útil si lo necesitas luego)
    optionElement.dataset.color = value.color;

    if (value.id === defaultValue) {
      optionElement.selected = true;
      colorBox.style.backgroundColor = value.color;
    }

    select.appendChild(optionElement);
  });

  // Al cambiar la selección
  select.onchange = (evt) => {
    const selectedId = Number(evt.target.value);

    const [selectedName, selectedData] = Array.from(map.entries()).find(
      ([, v]) => v.id === selectedId
    );

    colorBox.style.backgroundColor = selectedData.color;

    onSelectedValueChange(selectedId, selectedName, selectedData.color);
  };

  wrapper.appendChild(select);
  container.appendChild(wrapper);
}





// /**
//  * Function to add a dropdown menu for selecting annotation classes.
//  * 
//  * This function dynamically creates a dropdown (`<select>`) element in the specified container.
//  * It accepts a `Map` where keys are display names and values are the corresponding annotation class values.
//  * When the user selects an option, a callback function is triggered with the selected value and name.
//  *
//  * @param {Object} options - Configuration object.
//  * @param {string} id - The ID for the dropdown element.
//  * @param {Object} options - Dropdown options.
//  * @param {Map} options.map - A `Map` where keys are labels and values are option values.
//  * @param {number} options.defaultValue - The default selected value.
//  * @param {HTMLElement} container - The container where the dropdown will be appended.
//  * @param {Object} [style] - Optional styles for the dropdown.
//  * @param {string} [labelText - Optional label text to display before the dropdown.
//  * @param {Function} onSelectedValueChange - Callback function triggered when the selection changes.
//  */
// export function addDropdownToToolbarAnnotationClasses({
//   id,
//   options,
//   container,
//   style,
//   labelText,
//   onSelectedValueChange,
// }) {
//   const { map, defaultValue } = options; // Accepts a `Map` directly

//   // Set the container, defaulting to the element with ID 'toolbox' if not provided
//   container = container ?? document.getElementById('toolbox');

//   // Create a label if labelText is provided
//   if (labelText) {
//     const label = document.createElement('label');
//     label.htmlFor = id;
//     label.innerText = labelText;
//     container.append(label);
//   }

//   // Create the `<select>` dropdown element
//   const select = document.createElement('select');
//   select.id = id;

//   // Apply styles if provided
//   if (style) {
//     Object.assign(select.style, style);
//   }

//   // Populate the dropdown with options from the `Map`
//   Array.from(map.entries()).forEach(([key, value]) => {
//     const optionElement = document.createElement('option');
//     optionElement.value = value; // Assign the value from the `Map`
//     optionElement.innerText = key; // Assign the display name from the `Map`
//     if (value === defaultValue) {
//       optionElement.selected = true; // Set the default selected value
//     }
//     select.append(optionElement); // Append the option to the dropdown
//   });

//   // Assign an event listener for when the selection changes
//   select.onchange = (evt) => {
//     const selectElement = evt.target;
//     const selectedValue = Number(selectElement.value); // Get the selected value
//     const selectedName = Array.from(map.entries()).find(([v]) => v === selectedValue)?.[0]; // Find the corresponding name
//     onSelectedValueChange(selectedValue, selectedName); // Call the callback function with the value and name
//   };

//   // Append the dropdown to the specified container
//   container.append(select);
// }

/**
 * Function to add a dropdown menu for selecting annotation tools.
 * 
 * This function dynamically creates a dropdown (`<select>`) element in the specified container.
 * It allows users to select annotation tools from a given list or a `Map` object.
 * When an option is selected, a callback function is triggered with the selected value and its associated data.
 *
 * @param {Object} options - Configuration object.
 * @param {string} id - The ID for the dropdown element.
 * @param {Object} options - Dropdown options.
 * @param {Map} [options.map] - A `Map` where keys are labels and values are option data (optional).
 * @param {Array} [options.values] - An array of values to populate the dropdown (default: map keys).
 * @param {number|string} [options.defaultValue] - The default selected value.
 * @param {number} [options.defaultIndex] - The index of the default selected option if `defaultValue` is undefined.
 * @param {HTMLElement} [container] - The container where the dropdown will be appended.
 * @param {Object} [style] - Optional styles for the dropdown.
 * @param {Function} onSelectedValueChange - Callback function triggered when the selection changes.
 * @param {string} [labelText] - Optional label text displayed before the dropdown.
 */
export function addDropdownToToolbarAnnotationTool({
  id,
  options,
  container,
  style,
  onSelectedValueChange,
  labelText,
}) {
  const {
    map,
    values = [...map.keys()],
    defaultValue,
    defaultIndex = defaultValue === undefined && 0,
  } = options;
  container = container ?? document.getElementById("toolbox");


  // Create label element if labelText is provided
  if (labelText) {
    const label = document.createElement('label');
    label.htmlFor = id;
    label.innerText = labelText;
    container.append(label);
  }

  const select = document.createElement('select');
  select.id = id;

  if (style) {
    Object.assign(select.style, style);
  }

  values.forEach((value, index) => {
    const optionElement = document.createElement('option');
    const stringValue = String(value);
    optionElement.value = stringValue;
    optionElement.innerText = stringValue;
    if (value === defaultValue || index === defaultIndex) {
      optionElement.selected = true;
      if (map) {
        map.get(value).selected = true;
      }
    }
    select.append(optionElement);
  });

  select.onchange = (evt) => {
    const selectElement = evt.target;
    const { value: key } = selectElement;
    if (selectElement) {
      onSelectedValueChange(key, map?.get(key));
    }
  };

  container.append(select);
}

/**
 * Function to initialize a button with tooltip functionality.
 *
 * @param {string} containerId - ID of the button container.
 * @param {string} buttonId - ID of the button inside the container.
 * @param {string} tooltipId - ID of the tooltip element.
 * @param {string} [tooltipKey] - i18n key for the tooltip text (re-renders on language change).
 * @param {string} [tooltipText] - Static tooltip text (legacy, no i18n).
 * @param {Function} onClick - Callback function triggered when the button is clicked.
 */
export function initializeButtonWithTooltip({
    containerId,
    buttonId,
    tooltipId,
    tooltipKey,
    tooltipText,
    onClick
  }) {
    const container = document.getElementById(containerId);
    const button = container?.querySelector(`#${buttonId}`);
    const tooltip = container?.querySelector(`#${tooltipId}`);

    if (!container || !button || !tooltip) {
      console.error(`Error: One or more elements are missing for button ${buttonId}`);
      return;
    }

    // Set the tooltip text. If tooltipKey is given, translate and subscribe to
    // language changes so the tooltip stays in sync.
    const applyText = () => {
      tooltip.textContent = tooltipKey ? t(tooltipKey) : (tooltipText ?? '');
    };
    applyText();
    if (tooltipKey) {
      window.addEventListener('language-changed', applyText);
    }

    // Assign the provided click event function
    button.onclick = () => {
      if (onClick && typeof onClick === 'function') {
        onClick(button);
      }
    };

    let tooltipTimeout;

    // Show the tooltip after 1.25 seconds on hover
    container.addEventListener('mouseover', () => {
      tooltipTimeout = setTimeout(() => {
        tooltip.classList.add('active'); // Show tooltip
      }, 1250); // 1.25 seconds
    });

    // Hide the tooltip when the cursor leaves the button
    container.addEventListener('mouseout', () => {
      clearTimeout(tooltipTimeout); // Cancel the timer
      tooltip.classList.remove('active'); // Hide tooltip
    });
  }

/**
 * Displays a loading indicator with animated dots.
 * @returns {number} loadingInterval - The interval ID used to control the animation.
 */
export function showLoadingIndicator() {
  // Get the DOM elements for the loading message and the dots
  const loadingDiv = document.getElementById('loading-indicator');
  const dotsSpan = document.getElementById('loading-dots');

  // Make the loading indicator visible
  loadingDiv.style.display = 'block';

  // Start with 1 dot
  let dots = 1;

  // Animate the dots: ".", "..", "...", then repeat
  const loadingInterval = setInterval(() => {
    dots = (dots % 3) + 1;
    dotsSpan.textContent = '.'.repeat(dots);
  }, 500);

  return loadingInterval;
}

/**
 * Hides the loading indicator and stops the dot animation.
 * @param {number} loadingInterval - The interval ID used for the dot animation.
 */
export function hideLoadingIndicator(loadingInterval) {
  const loadingDiv = document.getElementById('loading-indicator');

  // Hide the loading indicator
  loadingDiv.style.display = 'none';

  // Clear the animation interval if it exists
  if (loadingInterval) {
    clearInterval(loadingInterval);
  }
}